import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CustomAudioPlayer.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";

import {
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
} from "@fortawesome/free-solid-svg-icons";
import playerVideo from "../../assets/customplayer.mp4";

const CustomAudioPlayer = ({ src, title }) => {
  const audioRef = useRef(null);
  const videoRef = useRef(null); // Viite videoon
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    const video = videoRef.current;

    if (audio.paused) {
      audio.play();
      video && video.play(); // Käynnistetään video jos löytyy
      setIsPlaying(true);
    } else {
      audio.pause();
      video && video.pause(); // Pysäytetään video
      setIsPlaying(false);
    }
  };

  const handlePlaybackRateChange = () => {
    const speeds = [0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newRate = speeds[nextIndex];
    audioRef.current.playbackRate = newRate;
    setPlaybackRate(newRate);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    audioRef.current.volume = newVolume;
    setVolume(newVolume);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    audio.muted = !audio.muted;
    setVolume(audio.muted ? 0 : audio.volume);
  };

  const handleSeek = (e) => {
    const timeline = e.currentTarget;
    const rect = timeline.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / rect.width;
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleKeyDownSeek = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      handleSeek(e);
    }
  };

  const formatTime = (time) => {
    const hrs = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = Math.floor(time % 60);
    return `${hrs > 0 ? hrs + ":" : ""}${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const getTitleFromSrc = () => {
    try {
      const urlObj = new URL(src);
      const filename = urlObj.pathname.split("/").pop()?.replace(".mp3", "") || "";
      const parts = decodeURIComponent(filename).split("_-_");
      if (parts.length === 2) {
        const artist = parts[0].replace(/_/g, " ").trim();
        const song = parts[1].replace(/_/g, " ").trim();
        return `${artist} – ${song}`;
      }
      return filename.replace(/_/g, " ");
    } catch {
      return "Tuntematon kappale";
    }
  };

  const displayTitle = title || getTitleFromSrc();

  return (
    <div className="custom-audio-player relative overflow-hidden">
      {/* Taustavideo */}
      <video
  ref={videoRef}
  className="absolute top-0 left-0 w-full h-full object-cover z-0"
  src={playerVideo} // ilman lainausmerkkejä
  muted
  loop
  playsInline
/>


      {/* Sisältö kerroksen päällä */}
      <div className="relative z-10">
        <div className="overflow-hidden w-full">
          <div className="track-title animate-marquee whitespace-nowrap font-customFont1 text-shadow-lg text-left text-primary dark:text-primary text-lg xxsm:text-base font-base py-2">
            {displayTitle}
          </div>
        </div>

        <audio ref={audioRef} src={src} preload="metadata">
          <track kind="captions" src="" label="captions" default />
        </audio>

        <div
          className="timeline"
          onClick={handleSeek}
          onKeyDown={handleKeyDownSeek}
          tabIndex="0"
          role="button"
          aria-label="Seek timeline"
        >
          <div
            className="timeline-progress"
            style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
          ></div>
        </div>

        <div className="time-info font-customFont1 text-shadow-xxl text-left text-primary dark:text-primary text-lg xxsm:text-base font-light">
          <span>{formatTime(currentTime)}</span>
          <span>/</span>
          <span>{formatTime(duration)}</span>
        </div>

        <div className="controls flex justify-center gap-4">
  <motion.button
    onClick={togglePlayPause}
    whileTap={{ scale: 0.9 }}
    whileHover={{ scale: 1.1 }}
    className="control-button"
  >
    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
  </motion.button>

  <motion.button
    onClick={handlePlaybackRateChange}
    whileTap={{ scale: 0.9 }}
    whileHover={{ scale: 1.1 }}
    className="control-button speed-button"
  >
    {playbackRate}x
  </motion.button>

  <motion.button
    onClick={toggleMute}
    whileTap={{ scale: 0.9 }}
    whileHover={{ scale: 1.1 }}
    className="control-button"
  >
    <FontAwesomeIcon icon={volume === 0 ? faVolumeMute : faVolumeUp} />
  </motion.button>

  <input
    type="range"
    min="0"
    max="1"
    step="0.01"
    value={volume}
    onChange={handleVolumeChange}
    className="volume-slider"
  />
</div>

      </div>
    </div>
  );
};

CustomAudioPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export default CustomAudioPlayer;
